import Base, { EncodingState } from 'core/pages/Encoding/EncodingEncode'
import { Encodings, TmrTag, WorkOrder, WorkOrderRow } from 'stylewhere/api'
import { PreEncodingCorrectionModal, PreEncodingCorrectionPhase } from 'custom/gucci/components'
import { EncodingExtensions } from 'custom/gucci/extensions'
import {
  getPreEncodingCorrectionTagPassword,
  PreEncodingDuplicateRoleContext,
  swapPreEncodingSideDigits
} from 'custom/gucci/shared/preEncodingCorrection'
import { OperationReadingProps } from 'core/shared/OperationReadingProvider'
import { FormSchemaData, RfidReader } from 'stylewhere/shared'
import { __, T } from 'stylewhere/shared/i18n'
import {
  askUserConfirmation,
  getAskUserInputEveryTime,
  isCanEncodeWithOverQuantity,
  isCheckEncodedQuantity,
  isOrderCompleted,
  MAX_WRITE_ATTEMPT,
  showToast,
  showToastError
} from 'stylewhere/shared/utils'
import {
  getMissingPreEncodingBehaviour,
  GUCCI_MISSING_PREENCODING_ERROR,
  hasGucciMissingPreEncodingError,
  hasOnlyMissingStartEntityError,
  isReadyForSizeSelection,
  isTagValidationBeforeSizeSelection,
  shouldSuppressMissingPreEncodingKoSound
} from 'custom/gucci/shared/utils'

const PRE_ENCODING_CORRECTION_READ_MS = 2000

interface GucciEncodingState extends EncodingState {
  preEncodingCorrectionOpen: boolean
  preEncodingCorrectionPhase: PreEncodingCorrectionPhase
  preEncodingCorrectionInstruction: string
  preEncodingCorrectionEpc?: string
  preEncodingCorrectionTagsRead: TmrTag[]
  tagsReadyForSizeSelection: boolean
}

export default class EncodingEncode extends Base<OperationReadingProps<GucciEncodingState>, GucciEncodingState> {
  preEncodingCorrectionTimer: ReturnType<typeof setTimeout> | null = null
  private savedTagReadCallback: ((tag: TmrTag, antennaId?: string) => Promise<void>) | null = null
  private preEncodingCorrectionTagsCollected: TmrTag[] = []
  private isPreEncodingCorrectionSession = false
  private suppressMissingPreEncodingKoSound = false
  // work order con più taglie su cui i tag vengono validati prima della scelta taglia
  private tagValidationWorkOrder?: WorkOrder

  constructor(props: OperationReadingProps<GucciEncodingState>) {
    super(props)
    this.state = {
      ...this.state,
      preEncodingCorrectionOpen: false,
      preEncodingCorrectionPhase: 'reading',
      preEncodingCorrectionInstruction: '',
      preEncodingCorrectionEpc: undefined,
      preEncodingCorrectionTagsRead: [],
      tagsReadyForSizeSelection: false
    }

    const baseOnTagRead = this.onTagRead.bind(this)
    this.onTagRead = async (tag: TmrTag, antennaId?: string) => {
      if (this.isPreEncodingCorrectionSession) {
        // avoids identifier box re-rendering with wrong data during correction process
        this.removeTagReadFromReader([tag])
        return
      }
      return baseOnTagRead(tag, antennaId)
    }

    const baseAdditionalOnTagReadCheck = this.additionalOnTagReadCheck.bind(this)
    this.additionalOnTagReadCheck = (tag: TmrTag) => {
      if (this.isPreEncodingCorrectionSession) return false
      return baseAdditionalOnTagReadCheck(tag)
    }

    const baseCheckIfForceCreate = this.checkIfForceCreate.bind(this)
    this.checkIfForceCreate = (value: string) => {
      const errors = this.state.encodingValidationResponse?.errors ?? []
      if (!hasGucciMissingPreEncodingError(errors)) {
        return baseCheckIfForceCreate(value)
      }
      if (errors.some((error) => !error.forcible)) return false

      const hasOtherForcibleErrors = errors.some(
        (error) => error.errorCode !== GUCCI_MISSING_PREENCODING_ERROR
      )
      if (hasOtherForcibleErrors) {
        return baseCheckIfForceCreate(value)
      }

      const behaviour = getMissingPreEncodingBehaviour()
      if (behaviour === 'warnWithUserConfirmation') {
        return value === 'withUserConfirmation'
      }
      if (behaviour === 'ignore' || behaviour === 'warnWithAutoconfirm') {
        return value === 'yes'
      }
      return baseCheckIfForceCreate(value)
    }

    const baseCallEncodingWithConfirmation = this.call_encoding_with_confirmation.bind(this)
    this.call_encoding_with_confirmation = async (write: boolean) => {
      const errors = this.state.encodingValidationResponse?.errors ?? []
      const preEncodingError = errors.find((error) => error.errorCode === GUCCI_MISSING_PREENCODING_ERROR)
      const behaviour = getMissingPreEncodingBehaviour()
      const onlyGucciMissingPreEncoding =
        errors.length > 0 && errors.every((error) => error.errorCode === GUCCI_MISSING_PREENCODING_ERROR)

      if (
        preEncodingError &&
        behaviour === 'warnWithUserConfirmation' &&
        onlyGucciMissingPreEncoding &&
        !errors.some((error) => !error.forcible)
      ) {
        const ok = await askUserConfirmation(
          __(T.misc.attention),
          EncodingExtensions.getMissingPreEncodingConfirmationMessage(preEncodingError)
        )
        if (ok) {
          this.trackForcibleErrorsModalClick(errors)
          this.call_force_encoding(write)
        } else {
          this.clear()
          this.isEncoding = false
        }
        return
      }

      return baseCallEncodingWithConfirmation(write)
    }

    const baseOnTagReadValidateResponse = this.onTagReadValidateResponse.bind(this)
    this.onTagReadValidateResponse = async () => {
      const response = this.state.encodingValidationResponse
      const waitingForSizeSelection = this.isWaitingForSizeSelection()
      if (waitingForSizeSelection) {
        const ready = isReadyForSizeSelection(response)
        this.setState({ tagsReadyForSizeSelection: ready })
        if (ready) {
          // tag ammissibili: si attende la scelta taglia, con antenne accese
          this.isEncoding = false
          return
        }
      }
      this.suppressMissingPreEncodingKoSound =
        shouldSuppressMissingPreEncodingKoSound(response?.errors) ||
        (waitingForSizeSelection && hasOnlyMissingStartEntityError(response?.errors))
      await baseOnTagReadValidateResponse()
      this.suppressMissingPreEncodingKoSound = false
    }

    const baseFeedbackSoundKo = this.feedbackSoundKo.bind(this)
    this.feedbackSoundKo = () => {
      if (this.suppressMissingPreEncodingKoSound) return
      baseFeedbackSoundKo()
    }

    const baseWriteSuccess = this.writeSuccess
    this.writeSuccess = () => {
      if (this.state.preEncodingCorrectionOpen) {
        showToast({
          status: 'success',
          description: __('pre_encoding.correction_write_success'),
          sound: this.state.enableOkSounds
        })
        void this.closePreEncodingCorrectionAfterSuccess()
        return
      }
      baseWriteSuccess()
    }

    const baseWriteTagError = this.writeTagError
    this.writeTagError = async () => {
      if (this.state.preEncodingCorrectionOpen) {
        const { numWriteAttempt } = this.state
        if (numWriteAttempt === MAX_WRITE_ATTEMPT) {
          this.feedbackSoundKo()
          this.setState({ preEncodingCorrectionPhase: 'write_error', processing: false })
          return
        }
        this.setState(
          (prevState) => ({
            numWriteAttempt: numWriteAttempt + 1,
            writeRetryCount: prevState.writeRetryCount + 1
          }),
          this.writeTag
        )
        return
      }
      return baseWriteTagError()
    }

    const baseOnWorkOrderReceived = this.onWorkOrderReceived
    this.onWorkOrderReceived = async (workOrder: WorkOrder, workOrderRows?: WorkOrderRow[]) => {
      // impostato prima del caricamento dei contatori, così la griglia è subito disabilitata
      // (con una sola taglia resta il flusso attuale, taglia preselezionata)
      this.tagValidationWorkOrder =
        isTagValidationBeforeSizeSelection(this.operation) && (workOrderRows?.length ?? 0) > 1 ? workOrder : undefined
      this.setState({ tagsReadyForSizeSelection: false })
      await baseOnWorkOrderReceived(workOrder)
      if (this.tagValidationWorkOrder) this.restartTagValidationWithoutSize()
    }

    const baseOnSubmitForm = this.onSubmitForm
    this.onSubmitForm = async (formSchemaData: FormSchemaData) => {
      if (!this.isWaitingForSizeSelection()) return baseOnSubmitForm(formSchemaData)
      await this.validateWithSelectedSize(formSchemaData)
    }

    const baseClearReads = this.clearReads
    this.clearReads = async (addtags = false, noask = false) => {
      if (this.tagValidationWorkOrder) {
        // torna subito in attesa della taglia: griglia disabilitata finché la nuova validate senza taglia non è pronta
        this.setState((prevState) => ({
          tagsReadyForSizeSelection: false,
          encodingValidation: prevState.encodingValidation
            ? { ...prevState.encodingValidation, startEntityId: undefined }
            : prevState.encodingValidation
        }))
      }
      // stesso criterio del core: con askUserInputEveryTime (e senza noask) viene resettato anche il work order
      if (getAskUserInputEveryTime(this.operation) && !noask) this.tagValidationWorkOrder = undefined
      await baseClearReads(addtags, noask)
      if (this.tagValidationWorkOrder) this.restartTagValidationWithoutSize()
    }

    const baseRetry = this.retry
    this.retry = async () => {
      // dopo la scelta taglia, riprova torna alla lettura dei tag senza taglia
      if (this.tagValidationWorkOrder && this.state.encodingValidation?.startEntityId) {
        this.trackDatadogAction('clicOnRetryButton')
        await this.clearReads(false, true)
        return
      }
      return baseRetry()
    }
  }

  isWaitingForSizeSelection = () => !!this.tagValidationWorkOrder && !this.state.encodingValidation?.startEntityId

  getDisableWorkOrderRowSelection() {
    return this.isWaitingForSizeSelection() && !this.state.tagsReadyForSizeSelection
  }

  // la callback parte dopo gli aggiornamenti di stato già accodati (es. contatori del work order)
  restartTagValidationWithoutSize = () => {
    this.setState({ tagsReadyForSizeSelection: false }, () => void this.startTagValidationWithoutSize())
  }

  startTagValidationWithoutSize = async () => {
    const workOrder = this.tagValidationWorkOrder
    if (
      !workOrder ||
      isOrderCompleted(
        this.state.counters,
        isCheckEncodedQuantity(this.operation),
        isCanEncodeWithOverQuantity(this.operation)
      )
    ) {
      return
    }
    const formSchemaData = { workOrder: workOrder.code, workOrderId: workOrder.id }
    try {
      this.initializeDataDogSession(formSchemaData)
      // senza work order row il BE restituisce una configurazione fissa e l'errore GUCCI_MISSING_START_ENTITY
      const res = await EncodingExtensions.getItemConfiguration(
        this.operation,
        formSchemaData,
        undefined,
        this.datadogSession?.runId
      )
      if (this.tagValidationWorkOrder !== workOrder) return
      this.setState(
        {
          encodingValidation: res.encodingValidation,
          encodingValidationResponse: res.encodingValidationResponse,
          formSchemaData
        },
        () => {
          if (!RfidReader.isReading()) this.checkStartReaderAfterItemConfiguration()
        }
      )
    } catch (error) {
      this.showToastErrorWithDatadog(error, __(T.error.error), this.isModal, {
        enableKoSounds: this.state.enableKoSounds
      })
    }
  }

  validateWithSelectedSize = async (formSchemaData: FormSchemaData) => {
    const { encodingValidation, tagsReadyForSizeSelection } = this.state
    const workOrderRowId = formSchemaData.wordOrderRowId
    // con una validate in corso l'esito dei tag può cambiare: la taglia va riselezionata
    if (this.isEncoding || !tagsReadyForSizeSelection || !encodingValidation || !workOrderRowId) {
      this.encodingFormRef.current?.resetSelectedWorkOrderRowId()
      return
    }
    this.clearTimer()
    // blocca le validate da nuove letture finché non parte quella con la taglia
    this.isEncoding = true
    try {
      const counters = (await Encodings.getWorkOrderRowCounter(workOrderRowId)).data
      this.setState(
        {
          encodingValidation: { ...encodingValidation, startEntityId: workOrderRowId },
          counters,
          formSchemaData,
          tagsReadyForSizeSelection: false
        },
        () => {
          this.isEncoding = false
          void this.onTagReadValidate()
        }
      )
    } catch (error) {
      this.isEncoding = false
      this.encodingFormRef.current?.resetSelectedWorkOrderRowId()
      this.showToastErrorWithDatadog(error, __(T.error.error), this.isModal, {
        enableKoSounds: this.state.enableKoSounds
      })
    }
  }

  componentDidMount() {
    super.componentDidMount?.()
    document.addEventListener('pointerdown', this.onModalPointerDown, true)
    document.addEventListener('click', this.onModalCloseClick, true)
  }

  componentWillUnmount() {
    this.isPreEncodingCorrectionSession = false
    this.clearPreEncodingCorrectionTimer()
    super.componentWillUnmount?.()
    document.removeEventListener('pointerdown', this.onModalPointerDown, true)
    document.removeEventListener('click', this.onModalCloseClick, true)
  }

  getPreEncodingDuplicateRoleContext = (): PreEncodingDuplicateRoleContext | undefined =>
    EncodingExtensions.getPreEncodingDuplicateRoleContext(
      this.operation,
      this.state.encodingValidationResponse,
      this.state.encodingValidation
    )

  getAssociationMessage = (): string | undefined => {
    const context = this.getPreEncodingDuplicateRoleContext()
    if (!context || this.state.associationStatus !== 'ERROR') return undefined
    return EncodingExtensions.getPreEncodingDuplicateRoleAssociationMessage(context.duplicateRole)
  }

  getAssociationButtons = (): { text: string; onClick: () => void }[] | undefined => {
    const context = this.getPreEncodingDuplicateRoleContext()
    if (!context || this.state.associationStatus !== 'ERROR') return undefined
    return [{ text: __('pre_encoding.correct'), onClick: this.onPreEncodingCorrectClick }]
  }

  getDisableRetry = () => !!this.getPreEncodingDuplicateRoleContext()

  onStopCallbackIdentifierCheck(encodingValidation: EncodingState['encodingValidation']) {
    if (this.isPreEncodingCorrectionSession) return false
    return super.onStopCallbackIdentifierCheck(encodingValidation)
  }

  // pre-encoding correction: tagCorrectionWritePassword if configured, otherwise the same passwords as the core
  getTagWritePassword = async (code: string): Promise<string[]> => {
    const correctionPassword = this.state.preEncodingCorrectionOpen
      ? getPreEncodingCorrectionTagPassword(this.operation)
      : undefined
    return correctionPassword ? [correctionPassword] : EncodingExtensions.getTagWritePasswords(this.operation, code)
  }

  restoreEncodingFormFocus = () => {
    const focusEncodingForm = () => {
      const encodingForm = document.querySelector<HTMLInputElement>('#productionOrderRowSku')
      encodingForm?.focus()
    }

    requestAnimationFrame(() => {
      focusEncodingForm()
      setTimeout(focusEncodingForm, 100)
    })
  }

  onModalPointerDown = (event: PointerEvent) => {
    const target = event.target as HTMLElement | null
    if (!target) return

    const modalContent = document.querySelector<HTMLElement>('[data-testid="modal-content"]')
    if (!modalContent) return

    const clickedBackdrop = !!target.closest('[data-testid="modal-backdrop"]')
    const clickedOutsideModal = !target.closest('[data-testid="modal-content"]')

    if (clickedBackdrop || clickedOutsideModal) {
      this.restoreEncodingFormFocus()
    }
  }

  onModalCloseClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    if (!target) return

    const modalContent = document.querySelector<HTMLElement>('[data-testid="modal-content"]')
    if (!modalContent) return

    const clickedButton = target.closest('button')
    const buttonLabel = (clickedButton?.textContent ?? '').trim().toLowerCase()
    const clickedModalX = !!target.closest('[data-testid="modal-close-button"]')
    const clickedButtonIsValid =
      !!clickedButton &&
      modalContent.contains(clickedButton) &&
      (['ok', 'conferma', 'confirm', 'annulla', 'cancel'].includes(buttonLabel) || clickedModalX)

    if (!clickedButtonIsValid) return
    this.restoreEncodingFormFocus()
  }

  onResetDailyEncodedItems = () => {
    this.restoreEncodingFormFocus()
  }

  clearPreEncodingCorrectionTimer = () => {
    if (this.preEncodingCorrectionTimer) {
      clearTimeout(this.preEncodingCorrectionTimer)
      this.preEncodingCorrectionTimer = null
    }
  }

  restoreTagReadCallback = () => {
    if (this.savedTagReadCallback) {
      RfidReader.setOnTagReadCallback(this.savedTagReadCallback)
      this.savedTagReadCallback = null
    }
  }

  getPreEncodingCorrectionTagEpc = (tag: TmrTag): string => tag.epc ?? tag.code ?? ''

  onPreEncodingCorrectClick = async () => {
    const context = this.getPreEncodingDuplicateRoleContext()
    if (!context) return

    this.isPreEncodingCorrectionSession = true
    this.ensureDataDogSession()
    this.trackDatadogAction('preEncodingCorrectionClick', { duplicateRole: context.duplicateRole })

    await this.stopAntenna()
    this.clearPreEncodingCorrectionTimer()
    RfidReader.clear()

    this.setState({
      preEncodingCorrectionOpen: true,
      preEncodingCorrectionPhase: 'reading',
      preEncodingCorrectionInstruction: EncodingExtensions.getPreEncodingDuplicateRoleCorrectionInstruction(
        context.duplicateRole
      ),
      preEncodingCorrectionEpc: undefined,
      preEncodingCorrectionTagsRead: [],
      processing: false
    })

    void this.startPreEncodingCorrectionRead()
  }

  startPreEncodingCorrectionRead = async () => {
    this.preEncodingCorrectionTagsCollected = []
    this.savedTagReadCallback = this.isIdentifierFlow() ? this.onTagReadIdentifier : this.onTagRead
    RfidReader.setOnTagReadCallback(this.onPreEncodingCorrectionTagRead)
    RfidReader.setAutomaticStop(false)
    RfidReader.clear()

    try {
      await this.startAntenna('UHF')
      this.preEncodingCorrectionTimer = setTimeout(() => {
        void this.finishPreEncodingCorrectionRead()
      }, PRE_ENCODING_CORRECTION_READ_MS)
    } catch (error) {
      this.restoreTagReadCallback()
      this.setState({
        preEncodingCorrectionPhase: 'read_error',
        preEncodingCorrectionEpc: undefined,
        preEncodingCorrectionTagsRead: []
      })
      showToastError((error as Error)?.message ?? __('pre_encoding.correction_read_error'))
    }
  }

  onPreEncodingCorrectionTagReadDatadog = (tag: TmrTag) => {
    const epc = this.getPreEncodingCorrectionTagEpc(tag)
    if (!epc) return
    this.ensureDataDogSession()
    if (this.datadogSession) {
      if (!this.datadogSession.uhfRead) this.datadogSession.uhfRead = []
      this.datadogSession.uhfRead.push({
        offsetMs: this.getAntennaOffsetMs(true),
        uhfRead: epc
      })
      this.trackDatadogAction('uhfRead', { epc, phase: 'preEncodingCorrection' })
    }
  }

  onPreEncodingCorrectionTagRead = async (tag: TmrTag) => {
    const epc = this.getPreEncodingCorrectionTagEpc(tag)
    if (!epc) return
    if (
      this.preEncodingCorrectionTagsCollected.some((readTag) => this.getPreEncodingCorrectionTagEpc(readTag) === epc)
    ) {
      return
    }
    this.onPreEncodingCorrectionTagReadDatadog(tag)
    this.preEncodingCorrectionTagsCollected.push(tag)
  }

  finishPreEncodingCorrectionRead = async () => {
    if (!this.state.preEncodingCorrectionOpen) return

    this.clearPreEncodingCorrectionTimer()
    await this.stopAntenna()
    this.restoreTagReadCallback()

    const tagsRead = [...this.preEncodingCorrectionTagsCollected]
    if (tagsRead.length === 1) {
      const tag = tagsRead[0]
      const readEpc = this.getPreEncodingCorrectionTagEpc(tag)
      const epcToWrite = swapPreEncodingSideDigits(readEpc)
      const context = this.getPreEncodingDuplicateRoleContext()
      this.trackDatadogAction('preEncodingCorrectionEpcResolved', {
        readEpc,
        epcToWrite,
        duplicateRole: context?.duplicateRole
      })
      this.setState({
        preEncodingCorrectionPhase: 'epc_ready',
        preEncodingCorrectionEpc: epcToWrite,
        preEncodingCorrectionTagsRead: tagsRead,
        targetTag: readEpc,
        targetTid: tag.tid ?? '',
        tagToWrite: epcToWrite,
        numWriteAttempt: 1,
        writeRetryCount: 0
      })
      return
    }

    this.feedbackSoundKo()
    this.setState({
      preEncodingCorrectionPhase: 'read_error',
      preEncodingCorrectionEpc: undefined,
      preEncodingCorrectionTagsRead: tagsRead
    })
  }

  onPreEncodingCorrectionWrite = () => {
    this.trackDatadogAction('write', {
      phase: 'preEncodingCorrection',
      epcToWrite: this.state.preEncodingCorrectionEpc,
      targetTag: this.state.targetTag
    })
    // 'writing' first, so the write button is disabled while the passwords are read
    this.setState({ preEncodingCorrectionPhase: 'writing' }, async () => {
      // the correction is a new write: read its passwords once, its retries reuse them
      const tagWritePasswords = await this.getTagWritePassword(this.state.targetTag)
      // cancelled while the passwords were being read
      if (!this.state.preEncodingCorrectionOpen) return
      this.setState({ tagWritePasswords }, this.writeTag)
    })
  }

  onPreEncodingCorrectionRetryRead = () => {
    this.preEncodingCorrectionTagsCollected = []
    this.setState(
      {
        preEncodingCorrectionPhase: 'reading',
        preEncodingCorrectionEpc: undefined,
        preEncodingCorrectionTagsRead: []
      },
      () => void this.startPreEncodingCorrectionRead()
    )
  }

  onPreEncodingCorrectionRetryWrite = () => {
    this.setState({ preEncodingCorrectionPhase: 'writing', numWriteAttempt: 1, writeRetryCount: 0 }, this.writeTag)
  }

  onPreEncodingCorrectionCancel = () => {
    void this.closePreEncodingCorrectionAndReset()
  }

  closePreEncodingCorrectionAfterSuccess = async () => {
    this.isPreEncodingCorrectionSession = false
    this.clearPreEncodingCorrectionTimer()
    this.preEncodingCorrectionTagsCollected = []
    this.restoreTagReadCallback()
    this.writeTagStartTime = undefined

    this.setState({
      preEncodingCorrectionOpen: false,
      tagWritePasswords: [],
      preEncodingCorrectionPhase: 'reading',
      preEncodingCorrectionInstruction: '',
      preEncodingCorrectionEpc: undefined,
      preEncodingCorrectionTagsRead: []
    })

    await this.retry()
  }

  closePreEncodingCorrectionAndReset = async () => {
    this.isPreEncodingCorrectionSession = false
    this.clearPreEncodingCorrectionTimer()
    this.preEncodingCorrectionTagsCollected = []
    this.restoreTagReadCallback()
    this.writeTagStartTime = undefined

    this.setState({
      preEncodingCorrectionOpen: false,
      tagWritePasswords: [],
      preEncodingCorrectionPhase: 'reading',
      preEncodingCorrectionInstruction: '',
      preEncodingCorrectionEpc: undefined,
      preEncodingCorrectionTagsRead: []
    })

    await this.onClear()
    this.restoreEncodingFormFocus()
  }

  render() {
    const {
      preEncodingCorrectionOpen,
      preEncodingCorrectionPhase,
      preEncodingCorrectionInstruction,
      preEncodingCorrectionEpc
    } = this.state

    return (
      <>
        {super.render()}
        {preEncodingCorrectionOpen && (
          <PreEncodingCorrectionModal
            visible={preEncodingCorrectionOpen}
            instruction={preEncodingCorrectionInstruction}
            phase={preEncodingCorrectionPhase}
            epcLabel={preEncodingCorrectionEpc}
            onWrite={this.onPreEncodingCorrectionWrite}
            onRetryRead={this.onPreEncodingCorrectionRetryRead}
            onRetryWrite={this.onPreEncodingCorrectionRetryWrite}
            onCancel={this.onPreEncodingCorrectionCancel}
          />
        )}
      </>
    )
  }
}
